/**
 * Parsing + layout hints for organized-tour itinerary text on the public offer page.
 */

const HEB_DOW_GROUP =
  "(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)";

/** @typedef {{ title: string, subtitle?: string|null, rawHeading: string }} DayHeaderParsed */

/** @param {string} s */
export function normalizeItineraryText(s) {
  let t = String(s || "")
    .replace(/\uFEFF/g, "")
    .replace(/\r\n/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B\u200C\u200D\u2060]/g, "");

  t = t
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/(?:[·•\-\–—_]\s*){4,}/g, " ");

  const lines = t.split("\n").map((line) => {
    let L = line.replace(/ {2,}/g, " ").trim();
    if (/^(?:\.{3,}|_{3,}|-{3,}|–{3,}|—{3,})$/.test(L)) return "";
    if (/^\[(?:\s*ללא\s*|לא ידוע|N\/A|TBD|tbd)\]$/i.test(L)) return "";
    return L;
  });

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Paragraphs preferring blank-line breaks; preserves single-newline lines as distinct short blocks.
 * @param {string} raw
 */
export function textToParagraphBlocks(raw) {
  const text = normalizeItineraryText(raw);
  if (!text) return [];
  const byDouble = text.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  if (byDouble.length > 1) return byDouble;
  const singles = text.split("\n").map((p) => p.trim()).filter(Boolean);
  return singles.length ? singles : [];
}

/** @param {string} trimmed */
export function parseDayHeaderLine(trimmed) {
  if (!trimmed || trimmed.length > 420) return null;

  /** @type {null | DayHeaderParsed} */
  let m;

  const hebDow = new RegExp(`^יום\\s+${HEB_DOW_GROUP}(.*)$`, "iu");
  m = trimmed.match(hebDow);
  if (m) {
    const dow = String(m[1] || "").trim();
    let rest = String(m[2] || "").trim();
    rest = rest.replace(/^[\-–:]/, "").trim();
    const title = `יום ${dow}`;
    return { title, subtitle: rest || null, rawHeading: trimmed };
  }

  m = trimmed.match(/^היום\s+ה(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)(.*)$/iu);
  if (m) {
    const dow = String(m[1] || "").trim();
    let rest = String(m[2] || "").trim();
    rest = rest.replace(/^[\-–:]/, "").trim();
    return { title: `היום ה${dow}`, subtitle: rest || null, rawHeading: trimmed };
  }

  m = trimmed.match(/^יום\s*מס['׳]?\s*(\d+)(.*)$/iu);
  if (m) {
    const num = String(m[1]).trim();
    let rest = String(m[2] || "").trim();
    rest = rest.replace(/^[\-–:]/, "").trim();
    return { title: `יום מס׳ ${num}`, subtitle: rest || null, rawHeading: trimmed };
  }

  m = trimmed.match(/^יום\s*(\d+)\s*(?:[\-–:]\s*)?(.*)$/iu);
  if (m && m[1] != null) {
    const num = String(m[1]).trim();
    let rest = String(m[2] || "").trim();
    return { title: `יום ${num}`, subtitle: rest || null, rawHeading: trimmed };
  }

  m = trimmed.match(/^Day\s*(\d+)\s*(?:[\-–:]\s*)?(.*)$/iu);
  if (m && m[1] != null) {
    const num = String(m[1]).trim();
    let rest = String(m[2] || "").trim();
    return { title: `Day ${num}`, subtitle: rest || null, rawHeading: trimmed };
  }

  m = trimmed.match(/^(\d{1,2})\s*-\s+(?=\S)(.+)$/);
  if (m && m[1] != null && m[2] != null) {
    const n = Number(m[1]);
    if (n >= 1 && n <= 99) {
      const restLine = String(m[2]).trim();
      return { title: `יום ${n}`, subtitle: restLine || null, rawHeading: trimmed };
    }
  }

  m = trimmed.match(/^(\d{1,2})\.\s+(?=\S)(.+)$/);
  if (m && m[1] != null && m[2] != null) {
    const n = Number(m[1]);
    if (n >= 1 && n <= 99) {
      const restLine = String(m[2]).trim();
      return { title: `יום ${n}`, subtitle: restLine || null, rawHeading: trimmed };
    }
  }

  return null;
}

/**
 * @param {string} trimmed
 * @returns {null | { key: string, headingDisplay: string, sameLineTrail: string }}
 */
export function parseSectionHeaderLine(trimmed) {
  if (!trimmed) return null;

  m = trimmed.match(/^[:\s.\u2022•]*לא\s+כולל\b\s*[:\.]?\s*(.*)$/i);
  if (m) {
    const trail = String(m[1] || "").trim();
    return {
      key: "excluded",
      headingDisplay: "לא כולל",
      sameLineTrail: trail,
    };
  }

  if (/^כולל\s*$/i.test(trimmed)) {
    return { key: "included", headingDisplay: "כולל", sameLineTrail: "" };
  }

  m = trimmed.match(/^כולל\s*[:\.]\s*(.*)$/i);
  if (m && !/^יום\b/i.test(trimmed)) {
    const trail = String(m[1] || "").trim();
    return { key: "included", headingDisplay: "כולל", sameLineTrail: trail };
  }

  if (/^כלול\s*$/i.test(trimmed)) {
    return { key: "included", headingDisplay: "כולל", sameLineTrail: "" };
  }

  m = trimmed.match(/^כלול\s*[:\.]\s*(.*)$/i);
  if (m && !/^יום\b/i.test(trimmed)) {
    const trail = String(m[1] || "").trim();
    return { key: "included", headingDisplay: "כולל", sameLineTrail: trail };
  }

  m = trimmed.match(/^מסלול\b\s*[:\.]?\s*(.*)$/i);
  if (m) {
    const trail = String(m[1] || "").trim();
    return {
      key: "route",
      headingDisplay: "מסלול",
      sameLineTrail: trail,
    };
  }

  m = trimmed.match(/^ביטוחים\b\s*[:\.]?\s*(.*)$/i);
  if (m) {
    const trail = String(m[1] || "").trim();
    return {
      key: "insurance",
      headingDisplay: "ביטוחים",
      sameLineTrail: trail,
    };
  }

  m = trimmed.match(/^הערות\s*[:\.]?\s*(.*)$/i);
  if (m) {
    const trail = String(m[1] || "").trim();
    return {
      key: "notes",
      headingDisplay: "הערות",
      sameLineTrail: trail,
    };
  }

  m = trimmed.match(/^תנאים\s*[:\.]?\s*(.*)$/i);
  if (m) {
    const trail = String(m[1] || "").trim();
    return {
      key: "offer_terms_inline",
      headingDisplay: "תנאים (במסלול)",
      sameLineTrail: trail,
    };
  }

  return null;
}

const BULLET_LINE_RE = /^\s*(?:[•·◦●▪▫]{1,2}\s*|[\*]\s+|[\-\–—]\s+)/;

const INLINE_BULLET_SPLIT_RE = /\s*[•·]\s+/;

/**
 * @param {string} paragraph
 * @returns {Array<{ type: "p", text: string } | { type: "ul", items: string[] }>}
 */
export function splitParagraphIntoRichBlocks(paragraph) {
  const raw = String(paragraph || "").trim();
  if (!raw) return [];

  const lines = raw.split(/\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];

  /** @type {Array<{ type: "p", text: string } | { type: "ul", items: string[] }>} */
  const blocks = [];
  /** @type {string[] | null} */
  let curList = null;

  const stripBullet = (line) => line.replace(BULLET_LINE_RE, "").trim();

  const flushList = () => {
    if (curList && curList.length) {
      blocks.push({ type: "ul", items: curList });
    }
    curList = null;
  };

  const maybeInlineList = (singleLine) => {
    if (!INLINE_BULLET_SPLIT_RE.test(singleLine)) return null;
    const pieces = singleLine
      .split(INLINE_BULLET_SPLIT_RE)
      .map((p) => p.trim())
      .filter(Boolean);
    return pieces.length >= 2 ? pieces : null;
  };

  for (const line of lines) {
    if (BULLET_LINE_RE.test(line)) {
      if (!curList) curList = [];
      curList.push(stripBullet(line));
      continue;
    }
    flushList();
    const inline = maybeInlineList(line);
    if (inline) {
      blocks.push({ type: "ul", items: inline });
    } else {
      blocks.push({ type: "p", text: line });
    }
  }
  flushList();
  return blocks;
}

function mergeAdjacentProse(chunks) {
  /** @type {typeof chunks} */
  const out = [];
  for (const c of chunks) {
    const last = out[out.length - 1];
    if (c.kind === "prose" && last?.kind === "prose") {
      last.text = `${last.text}\n${c.text}`.trim();
    } else {
      out.push({ ...c });
    }
  }
  return out;
}

export const PUBLIC_OFFER_SUMMARY_SECTION_KEYS = new Set(["included", "excluded", "route", "notes"]);

const SUMMARY_SECTION_ORDER = /** @type {Record<string, number>} */ ({
  included: 0,
  excluded: 1,
  route: 2,
  notes: 3,
});

/**
 * @param {Array<{ type: string, key?: string }>} items
 */
export function partitionItinerarySummary(items) {
  /** @type {typeof items} */
  const summaryItems = [];
  /** @type {typeof items} */
  const timelineItems = [];
  for (const it of items) {
    if (it.type === "section" && it.key && PUBLIC_OFFER_SUMMARY_SECTION_KEYS.has(it.key)) {
      summaryItems.push(it);
    } else {
      timelineItems.push(it);
    }
  }
  summaryItems.sort(
    (a, b) =>
      (SUMMARY_SECTION_ORDER[String(a.key)] ?? 99) - (SUMMARY_SECTION_ORDER[String(b.key)] ?? 99)
  );
  return { summaryItems, timelineItems };
}

/**
 * @param {Array<{ type: string, paragraphs?: string[], key?: string }>} items
 */
export function countBulletListsInItineraryItems(items) {
  let bulletsCreated = 0;
  for (const it of items) {
    if (it.type !== "prose" && it.type !== "section" && it.type !== "day") continue;
    const paras = it.paragraphs || [];
    for (const p of paras) {
      for (const b of splitParagraphIntoRichBlocks(p)) {
        if (b.type === "ul") bulletsCreated += 1;
      }
    }
  }
  return bulletsCreated;
}

/**
 * @param {string} rawText
 * @returns {{
 *   items: Array<
 *     | { type: 'prose', paragraphs: string[] }
 *     | { type: 'section', key: string, title: string, paragraphs: string[] }
 *     | { type: 'day', title: string, subtitle?: string|null, paragraphs: string[] }
 *   >,
 *   summaryItems: Array<any>,
 *   timelineItems: Array<any>,
 *   daysDetected: number,
 *   sectionsDetected: number,
 *   fallbackUsed: boolean,
 *   parseStrategy: string,
 *   bulletsCreated: number,
 * }}
 */
export function extractOrganizedTourDailyItinerary(rawText) {
  const normalized = normalizeItineraryText(rawText);
  const textLen = normalized.length;

  if (!normalized) {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_PUBLIC_RENDER_PARSE_TRACE]", {
        detectedDays: 0,
        detectedSections: 0,
        bulletsCreated: 0,
        fallbackUsed: false,
        parseStrategy: "empty",
      });
    }
    return {
      items: [],
      summaryItems: [],
      timelineItems: [],
      daysDetected: 0,
      sectionsDetected: 0,
      fallbackUsed: false,
      parseStrategy: "empty",
      bulletsCreated: 0,
    };
  }

  const lines = normalized.split("\n");

  /** @type {Array<{kind:'prose'|'day'|'section', text:string, title?:string, subtitle?:string|null, key?:string, headingDisplay?: string}>} */
  const rawChunks = [];

  /** @type {string[]} */
  let proseBuf = [];
  /** @type {{title:string, subtitle?: string|null, lines:string[]}|null} */
  let currentDay = null;
  /** @type {{ key: string, headingDisplay: string, lines: string[] }|null} */
  let currentSection = null;

  const pushProse = () => {
    const t = proseBuf.join("\n").trim();
    if (t) rawChunks.push({ kind: "prose", text: t });
    proseBuf = [];
  };

  const closeDay = () => {
    if (!currentDay) return;
    const body = currentDay.lines.join("\n").trim();
    rawChunks.push({
      kind: "day",
      text: body,
      title: currentDay.title,
      subtitle: currentDay.subtitle || null,
    });
    currentDay = null;
  };

  const closeSection = () => {
    if (!currentSection) return;
    const body = currentSection.lines.join("\n").trim();
    rawChunks.push({
      kind: "section",
      text: body,
      key: currentSection.key,
      headingDisplay: currentSection.headingDisplay,
    });
    currentSection = null;
  };

  const closeAllStructured = () => {
    pushProse();
    closeDay();
    closeSection();
  };

  const startDay = (
    /** @type {DayHeaderParsed} */
    dh
  ) => {
    currentDay = { title: dh.title, subtitle: dh.subtitle ?? null, lines: [] };
  };

  const startSection = (
    /** @type {Required<NonNullable<ReturnType<typeof parseSectionHeaderLine>>>} */
    sh
  ) => {
    currentSection = {
      key: sh.key,
      headingDisplay: sh.headingDisplay,
      lines: [],
    };
    if (sh.sameLineTrail) {
      currentSection.lines.push(sh.sameLineTrail);
    }
  };

  /** שורות מסלול שנשארו בבלוק «לא כולל» — סוגרים את הסקשן וממשיכים בפרוזה */
  const lineLeaksItineraryIntoExcluded = (t) => {
    if (!t) return false;
    if (parseDayHeaderLine(t) || parseSectionHeaderLine(t)) return false;
    if (/לאחר\s+ארוחת\s+הבוקר|אחרי\s+ארוחת\s+הבוקר|לאחר\s+הבוקר|בבוקר\s+נצא|בבוקר\s+השכם/i.test(t)) {
      return true;
    }
  if (/^\s*(ניסע|נבקר|נצא|נסעו|נבקרו|נמשיך|נגיע|נצפה|נעבור|ניטייל|נעלה|נוריד)(?=\s|$|[,.])/i.test(t)) {
      return true;
    }
    if (/^\s*(נבקר\s+ב|ביקור\s+ב|סיור\s+ב)(?=\s|$|[,.])/i.test(t)) return true;
    if (/^transfer\b/i.test(t)) return true;
    if (/^flight\b/i.test(t)) return true;
    if (t.length >= 260 && /(מסלול|סיור|מלון|טיסה|שדה|נחיתה|המראה|לינה\s+ב|דרך\s+ל|חזרה\s+ל)/i.test(t)) {
      return true;
    }
    return false;
  };

  for (const lineRaw of lines) {
    const trimmed = lineRaw.trim();

    const dayHdr = trimmed ? parseDayHeaderLine(trimmed) : null;

    let secHdr = null;
    if (!dayHdr && trimmed) {
      secHdr = parseSectionHeaderLine(trimmed);
    }

    if (dayHdr) {
      closeAllStructured();
      startDay(dayHdr);
      continue;
    }

    if (secHdr) {
      closeAllStructured();
      startSection(secHdr);
      continue;
    }

    if (currentSection?.key === "excluded" && trimmed && lineLeaksItineraryIntoExcluded(trimmed)) {
      closeSection();
      proseBuf.push(lineRaw);
      continue;
    }

    if (currentDay) {
      currentDay.lines.push(lineRaw);
      continue;
    }
    if (currentSection) {
      currentSection.lines.push(lineRaw);
      continue;
    }

    proseBuf.push(lineRaw);
  }

  closeAllStructured();

  let mergedRaw = mergeAdjacentProse(rawChunks);

  const daysDetected = mergedRaw.filter((c) => c.kind === "day").length;
  const sectionsDetected = mergedRaw.filter((c) => c.kind === "section").length;

  /** אין כותרות יום — עדיין שומרים סקשנים (כולל/לא כולל וכו׳); רק פרוזה מקבל פירוק פסקאות מלא יותר */
  const fallbackUsed = daysDetected === 0 && textLen > 0;

  const items = mergedRaw.map((c) => {
    if (c.kind === "prose") {
      let paras = textToParagraphBlocks(c.text);
      if (fallbackUsed && paras.length <= 1 && (c.text || "").includes("\n")) {
        paras = (c.text || "")
          .split("\n")
          .map((p) => p.trim())
          .filter(Boolean);
      }
      return { type: "prose", paragraphs: paras };
    }
    if (c.kind === "section") {
      const paras = textToParagraphBlocks(c.text || "");
      return {
        type: "section",
        key: String(c.key),
        title: String(c.headingDisplay || c.key || ""),
        paragraphs: paras.length ? paras : [],
      };
    }
    return {
      type: "day",
      title: String(c.title || ""),
      subtitle: c.subtitle ?? null,
      paragraphs: textToParagraphBlocks(c.text || ""),
    };
  });

  const { summaryItems, timelineItems } = partitionItinerarySummary(items);
  const bulletsCreated = countBulletListsInItineraryItems(items);

  let parseStrategy = "prose_only";
  if (daysDetected >= 1) parseStrategy = "day_timeline";
  else if (sectionsDetected >= 1) parseStrategy = "sections_and_prose";

  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PUBLIC_RENDER_PARSE_TRACE]", {
      detectedDays: daysDetected,
      detectedSections: sectionsDetected,
      bulletsCreated,
      fallbackUsed,
      parseStrategy,
    });
  }

  return {
    items,
    summaryItems,
    timelineItems,
    daysDetected,
    sectionsDetected,
    fallbackUsed,
    parseStrategy,
    bulletsCreated,
  };
}
